const LOCATION_EVENT = 'mpt-native-location';
const PUSH_TOKEN_EVENT = 'mpt-native-push-token';
const BILLING_PRODUCTS_EVENT = 'mpt-native-billing-products';
const BILLING_PURCHASE_EVENT = 'mpt-native-billing-purchase';
const BILLING_RESTORE_EVENT = 'mpt-native-billing-restore';
const DEFAULT_TIMEOUT_MS = 8000;
const FULLSCREEN_CONTEXTS = new Set(['map', 'tv', 'dispatch-max', 'pass']);

let nativeFullscreenContext = null;

const getBridge = () => {
  if (typeof window === 'undefined') return null;
  return window.MPTNative || window.AndroidBridge || null;
};

const parseBridgeResult = (value) => {
  if (!value) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const createNativeError = (message, code) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const waitForNativeEvent = (eventName, invoke, timeoutMs = DEFAULT_TIMEOUT_MS) => new Promise((resolve, reject) => {
  if (typeof window === 'undefined') {
    reject(createNativeError('Bridge natif indisponible.', 'NATIVE_UNAVAILABLE'));
    return;
  }

  let timeoutId = null;

  const cleanup = () => {
    window.removeEventListener(eventName, handleEvent);
    if (timeoutId) window.clearTimeout(timeoutId);
  };

  const handleEvent = (event) => {
    const detail = event?.detail || {};
    cleanup();

    if (detail.status === 'error') {
      reject(createNativeError(detail.message || 'Erreur native.', detail.code || 'NATIVE_ERROR'));
      return;
    }

    resolve(detail);
  };

  window.addEventListener(eventName, handleEvent);
  timeoutId = window.setTimeout(() => {
    cleanup();
    reject(createNativeError('Réponse native trop lente.', 'NATIVE_TIMEOUT'));
  }, timeoutMs);

  try {
    invoke();
  } catch (error) {
    cleanup();
    reject(error);
  }
});

export const hasNativeBridge = () => {
  const bridge = getBridge();
  return Boolean(bridge && typeof bridge.getAppInfo === 'function');
};

export const hasNativeLocation = () => {
  const bridge = getBridge();
  return Boolean(bridge && typeof bridge.requestLocation === 'function');
};

export const hasNativePush = () => {
  const bridge = getBridge();
  return Boolean(bridge && typeof bridge.requestPushToken === 'function');
};

export const hasNativeBilling = () => {
  const bridge = getBridge();
  return Boolean(
    bridge
    && typeof bridge.requestBillingProducts === 'function'
    && typeof bridge.purchaseSubscription === 'function'
    && typeof bridge.restoreSubscriptions === 'function'
    && typeof bridge.openSubscriptionManagement === 'function'
  );
};

export const hasBrowserGeolocation = () => (
  typeof navigator !== 'undefined' && Boolean(navigator.geolocation)
);

export async function getNativeAppInfo() {
  const bridge = getBridge();
  if (!bridge || typeof bridge.getAppInfo !== 'function') return null;

  const info = parseBridgeResult(await bridge.getAppInfo());
  if (!info || info.wrapper !== true || info.platform !== 'android') return null;
  return info;
}

export async function requestNativePushToken(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const bridge = getBridge();
  if (!bridge || typeof bridge.requestPushToken !== 'function') {
    throw createNativeError('Notifications Android indisponibles.', 'NATIVE_PUSH_UNAVAILABLE');
  }

  const detail = await waitForNativeEvent(PUSH_TOKEN_EVENT, () => bridge.requestPushToken(), timeoutMs);
  if (!detail.token) {
    throw createNativeError('Token FCM vide.', 'NATIVE_PUSH_TOKEN_EMPTY');
  }

  return {
    transport: 'fcm',
    token: detail.token,
    platform: detail.platform || 'android',
    installationId: detail.installationId || null,
    appVersion: detail.appVersion || null,
  };
}

export async function requestNativeBillingProducts(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const bridge = getBridge();
  if (!bridge || typeof bridge.requestBillingProducts !== 'function') {
    throw createNativeError('Achats Google Play indisponibles.', 'NATIVE_BILLING_UNAVAILABLE');
  }

  return waitForNativeEvent(BILLING_PRODUCTS_EVENT, () => bridge.requestBillingProducts(), timeoutMs);
}

export async function purchaseNativeSubscription(productId, obfuscatedAccountId = null, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const bridge = getBridge();
  if (!bridge || typeof bridge.purchaseSubscription !== 'function') {
    throw createNativeError('Achats Google Play indisponibles.', 'NATIVE_BILLING_UNAVAILABLE');
  }

  const normalizedProductId = String(productId || '').trim();
  if (!normalizedProductId) {
    throw createNativeError('Produit Google Play manquant.', 'NATIVE_BILLING_PRODUCT_MISSING');
  }

  return waitForNativeEvent(
    BILLING_PURCHASE_EVENT,
    () => bridge.purchaseSubscription(normalizedProductId, obfuscatedAccountId || ''),
    timeoutMs
  );
}

export async function restoreNativeSubscriptions(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const bridge = getBridge();
  if (!bridge || typeof bridge.restoreSubscriptions !== 'function') {
    throw createNativeError('Restauration Google Play indisponible.', 'NATIVE_BILLING_UNAVAILABLE');
  }

  return waitForNativeEvent(BILLING_RESTORE_EVENT, () => bridge.restoreSubscriptions(), timeoutMs);
}

export function openNativeSubscriptionManagement(productId = '') {
  const bridge = getBridge();
  if (!bridge || typeof bridge.openSubscriptionManagement !== 'function') {
    throw createNativeError('Gestion Google Play indisponible.', 'NATIVE_BILLING_UNAVAILABLE');
  }

  const result = parseBridgeResult(bridge.openSubscriptionManagement(String(productId || ''))) || {};
  if (result.status === 'error') {
    throw createNativeError('Gestion Google Play indisponible.', result.code || 'NATIVE_BILLING_MANAGEMENT_ERROR');
  }
  return result;
}

export async function requestCurrentLocation(options = {}) {
  const bridge = getBridge();

  if (bridge && typeof bridge.requestLocation === 'function') {
    let detail;
    try {
      detail = await waitForNativeEvent(
        LOCATION_EVENT,
        () => bridge.requestLocation(),
        options.timeout || DEFAULT_TIMEOUT_MS
      );
    } catch (error) {
      if (error?.code === 'permission-denied') error.code = 1;
      if (error?.code === 'location-unavailable') error.code = 2;
      if (error?.code === 'location-error') error.code = 2;
      throw error;
    }

    return {
      latitude: Number(detail.latitude),
      longitude: Number(detail.longitude),
      accuracy: Number(detail.accuracy || 0),
      timestamp: detail.timestamp || Date.now(),
      source: 'android',
    };
  }

  if (!hasBrowserGeolocation()) {
    throw createNativeError('Géolocalisation indisponible.', 'GEOLOCATION_UNSUPPORTED');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
          source: 'browser',
        });
      },
      reject,
      options
    );
  });
}

export async function enterFullscreen(context, element = null) {
  const normalizedContext = String(context || '').trim().toLowerCase();
  if (!FULLSCREEN_CONTEXTS.has(normalizedContext)) {
    throw createNativeError('Contexte plein écran non supporté.', 'FULLSCREEN_CONTEXT_UNSUPPORTED');
  }

  const bridge = getBridge();
  if (bridge && typeof bridge.enterFullscreen === 'function') {
    const result = parseBridgeResult(await bridge.enterFullscreen(normalizedContext)) || {};
    if (result.status === 'error') {
      throw createNativeError('Plein écran Android refusé.', result.code || 'NATIVE_FULLSCREEN_ERROR');
    }
    nativeFullscreenContext = normalizedContext;
    return { native: true, context: normalizedContext };
  }

  const target = element || document.documentElement;
  if (!target?.requestFullscreen) {
    throw createNativeError('Plein écran indisponible.', 'FULLSCREEN_UNAVAILABLE');
  }

  await target.requestFullscreen();
  return { native: false, context: normalizedContext };
}

export async function exitFullscreen() {
  const bridge = getBridge();
  if (nativeFullscreenContext && bridge && typeof bridge.exitFullscreen === 'function') {
    const result = parseBridgeResult(await bridge.exitFullscreen()) || {};
    if (result.status === 'error') {
      throw createNativeError('Sortie plein écran Android refusée.', result.code || 'NATIVE_FULLSCREEN_ERROR');
    }
    nativeFullscreenContext = null;
    return { native: true };
  }

  nativeFullscreenContext = null;

  if (typeof document !== 'undefined' && document.fullscreenElement && document.exitFullscreen) {
    await document.exitFullscreen();
  }

  return { native: false };
}

export const isFullscreenActive = () => {
  if (nativeFullscreenContext) return true;
  return typeof document !== 'undefined' && Boolean(document.fullscreenElement);
};

export async function subscribeDeviceToNotifications({
  alert,
  language,
  deviceLabel,
  createAttractionNotification,
  subscribeBrowserToPush,
  serverVapidPublicKey,
}) {
  if (typeof createAttractionNotification !== 'function') {
    throw createNativeError('Client notifications invalide.', 'NOTIFICATION_CLIENT_MISSING');
  }

  if (hasNativePush()) {
    const nativeSubscription = await requestNativePushToken();
    return createAttractionNotification({
      subscription: {
        transport: 'fcm',
        token: nativeSubscription.token,
        platform: nativeSubscription.platform || 'android',
        installationId: nativeSubscription.installationId,
        appVersion: nativeSubscription.appVersion,
      },
      alert,
      language,
      deviceLabel: deviceLabel || 'Android app',
    });
  }

  if (typeof subscribeBrowserToPush !== 'function') {
    throw createNativeError('Web Push indisponible.', 'WEB_PUSH_UNAVAILABLE');
  }

  const subscription = await subscribeBrowserToPush(serverVapidPublicKey);
  return createAttractionNotification({
    subscription: subscription.toJSON(),
    alert,
    language,
    deviceLabel,
  });
}
